
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAppContext } from '../App';
import { Camera, MapPin, Edit2, Save, X, Briefcase, Book, Home, Search, Globe, Check, Users, UserPlus, UserCheck, Loader2, Sparkles, Navigation, Clock, UserMinus, ArrowRight, UploadCloud, MessageCircle, Heart, Share2, Trash2 } from 'lucide-react'; // Added missing icons
import { useParams, Link } from 'react-router-dom';
import { suggestBiblePassage, searchPlaces, reverseGeocode } from '../services/geminiService';
import { BibleVersion } from '../types';
import { supabase } from '../services/supabaseClient';

const Profile: React.FC = () => {
    const { 
        currentUser, updateUser, posts, churches, allUsers, 
        toggleConnection, incomingRequests, outgoingRequests, acceptConnectionRequest, removeConnection,
        lang, removePost, toggleLike // Added removePost and toggleLike from context
    } = useAppContext();
    const { userId } = useParams<{ userId: string }>();
    const [isEditing, setIsEditing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    
    // File inputs refs
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const bannerInputRef = useRef<HTMLInputElement>(null);

    // Determine which user profile to show
    const isOwnProfile = !userId || (currentUser && userId === currentUser.id);
    const profileUser = isOwnProfile ? currentUser : allUsers.find(u => u.id === userId);

    if (!currentUser || !profileUser) return <div className="p-10 text-center">User not found</div>;

    // Form state (only relevant if isOwnProfile)
    const [formData, setFormData] = useState({
        name: currentUser.name || '',
        voluntaryCall: currentUser.voluntaryCall || '',
        location: currentUser.location || '',
        bio: currentUser.bio || '',
        favoriteBiblePassage: currentUser.favoriteBiblePassage || '',
        churchId: currentUser.churchId || '',
        avatarUrl: currentUser.avatarUrl || '',
        bannerUrl: currentUser.bannerUrl || ''
    });

    // Church Selection State
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [selectedRegion, setSelectedRegion] = useState<string>('');
    const [churchSearch, setChurchSearch] = useState('');

    // Verse Search State
    const [verseSearchMode, setVerseSearchMode] = useState(false);
    const [verseQuery, setVerseQuery] = useState('');
    const [isSearchingVerse, setIsSearchingVerse] = useState(false);
    const [foundVerse, setFoundVerse] = useState<{reference: string, text: string} | null>(null);

    // Location Search State
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const [isLoadingGeo, setIsLoadingGeo] = useState(false);

    // Effect to sync formData when entering edit mode or when currentUser changes
    useEffect(() => {
        if (isEditing && currentUser) {
            setFormData({
                name: currentUser.name || '',
                voluntaryCall: currentUser.voluntaryCall || '',
                location: currentUser.location || '',
                bio: currentUser.bio || '',
                favoriteBiblePassage: currentUser.favoriteBiblePassage || '',
                churchId: currentUser.churchId || '',
                avatarUrl: currentUser.avatarUrl || '',
                bannerUrl: currentUser.bannerUrl || ''
            });
        }
    }, [isEditing, currentUser]);

    const church = churches.find(c => c.id === profileUser.churchId);
    
    // Filter and sort user posts (Newest First) using robust date parsing
    const userPosts = useMemo(() => {
        if (!posts || !profileUser) return [];
        return posts
            .filter(p => p.authorId === profileUser.id)
            // Filter out ghost posts (no content, no media, no bible citation)
            .filter(p => (p.content && p.content.trim().length > 0) || p.imageUrl || p.videoUrl || p.bibleCitation)
            .sort((a, b) => {
                const timeA = new Date(a.timestamp).getTime();
                const timeB = new Date(b.timestamp).getTime();
                // Handle invalid dates by treating them as very old (0)
                const validTimeA = isNaN(timeA) ? 0 : timeA;
                const validTimeB = isNaN(timeB) ? 0 : timeB;
                return validTimeB - validTimeA;
            });
    }, [posts, profileUser]);

    const connectionCount = profileUser.connections.length;
    
    // Connection Status Logic
    const isConnected = currentUser.connections.includes(profileUser.id);
    const isPendingOutgoing = outgoingRequests.includes(profileUser.id);
    const isPendingIncoming = incomingRequests.includes(profileUser.id);

    // Derived Lists for Filtering
    const availableCountries = Array.from(new Set(churches.map(c => c.country))).sort();
    const availableRegions = selectedCountry 
        ? Array.from(new Set(churches.filter(c => c.country === selectedCountry).map(c => c.region))).sort()
        : [];
    
    const filteredChurches = churches.filter(c => {
        if (selectedCountry && c.country !== selectedCountry) return false;
        if (selectedRegion && c.region !== selectedRegion) return false;
        if (churchSearch && !c.name.toLowerCase().includes(churchSearch.toLowerCase())) return false;
        return true;
    });

    const handleSave = () => {
        if (!isOwnProfile) return;
        updateUser({
            ...currentUser,
            ...formData
        });
        setIsEditing(false);
        setVerseSearchMode(false);
    };

    // Client-side resizing to ensure fast uploads even on slow networks
    const compressImage = (file: File, maxWidth: number): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = (maxWidth / width) * height;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);
                    
                    canvas.toBlob((blob) => {
                        if (blob) resolve(blob);
                        else reject(new Error("Canvas to Blob failed"));
                    }, 'image/jpeg', 0.8);
                };
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'avatarUrl' | 'bannerUrl') => {
        const file = e.target.files?.[0];
        if (!file || !currentUser) return;
        
        setIsUploading(true);

        try {
            // 1. Resize image before upload to save bandwidth and storage space
            const maxWidth = field === 'avatarUrl' ? 400 : 1200;
            const compressedBlob = await compressImage(file, maxWidth);
            
            // 2. Upload to Supabase Storage
            const fileExt = 'jpg';
            const fileName = `${currentUser.id}/${field}-${Date.now()}.${fileExt}`;
            
            const { data, error } = await supabase.storage
                .from('avatars')
                .upload(fileName, compressedBlob, {
                    contentType: 'image/jpeg',
                    upsert: true
                });

            if (error) throw error;

            // 3. Get Public URL
            const { data: { publicUrl } } = supabase.storage
                .from('avatars')
                .getPublicUrl(fileName);

            // 4. Update Local State
            setFormData(prev => ({ ...prev, [field]: publicUrl }));

        } catch (error: any) {
            console.error("Upload failed:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleVerseSearch = async () => {
        if (!verseQuery) return;
        setIsSearchingVerse(true);
        const result = await suggestBiblePassage(verseQuery, BibleVersion.NIV, lang);
        setFoundVerse(result);
        setIsSearchingVerse(false);
    };

    const applyVerse = () => {
        if (foundVerse) {
            setFormData({ ...formData, favoriteBiblePassage: `${foundVerse.text} (${foundVerse.reference})` });
            setVerseSearchMode(false);
            setFoundVerse(null);
            setVerseQuery('');
        }
    };

    // Debounced location search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (formData.location.length > 2 && showLocationSuggestions) {
                setIsSearchingLocation(true);
                const results = await searchPlaces(formData.location);
                setLocationSuggestions(results);
                setIsSearchingLocation(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.location, showLocationSuggestions]);

    const handleGeolocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser");
            return;
        }
        setIsLoadingGeo(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            const address = await reverseGeocode(latitude, longitude);
            setFormData(prev => ({ ...prev, location: address }));
            setIsLoadingGeo(false);
            setShowLocationSuggestions(false);
        }, (error) => {
            console.error(error);
            alert("Unable to retrieve your location");
            setIsLoadingGeo(false);
        });
    };

    // Display image source: if editing and local change exists, use that. Otherwise use current profile.
    const displayAvatar = isEditing ? formData.avatarUrl : profileUser.avatarUrl;
    const displayBanner = isEditing ? formData.bannerUrl : (profileUser.bannerUrl || "https://picsum.photos/seed/default_banner/1000/400");

    return (
        <div className="space-y-6">
            {/* Hidden File Inputs */}
            <input 
                type="file" 
                ref={avatarInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={(e) => handleImageUpload(e, 'avatarUrl')}
            />
            <input 
                type="file" 
                ref={bannerInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={(e) => handleImageUpload(e, 'bannerUrl')}
            />

            {/* Incoming Requests Section (Only on Own Profile) */}
            {isOwnProfile && incomingRequests.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 animate-fadeIn">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="text-blue-600" /> Connection Requests
                    </h3>
                    <div className="grid gap-4 md:grid-cols-2">
                        {incomingRequests.map(requesterId => {
                            const requester = allUsers.find(u => u.id === requesterId);
                            if (!requester) return null;
                            return (
                                <div key={requester.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg bg-gray-50">
                                    <img src={requester.avatarUrl} alt="" className="w-12 h-12 rounded-full object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-900 truncate">{requester.name}</p>
                                        <p className="text-xs text-gray-500 truncate">{requester.voluntaryCall}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => acceptConnectionRequest(requester.id)}
                                            className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded hover:bg-blue-700"
                                        >
                                            Accept
                                        </button>
                                        <button 
                                            onClick={() => removeConnection(requester.id)}
                                            className="bg-white border border-gray-300 text-gray-600 text-xs font-bold px-3 py-1.5 rounded hover:bg-gray-100"
                                        >
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Header / Hero */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <div className="h-48 md:h-64 bg-gray-300 relative group">
                    <img src={displayBanner} alt="Cover" className="w-full h-full object-cover" />
                    {isEditing && isOwnProfile && (
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                            <button 
                                onClick={() => bannerInputRef.current?.click()}
                                disabled={isUploading}
                                className="bg-white/20 backdrop-blur text-white px-4 py-2 rounded-full hover:bg-white/30 transition font-bold flex items-center gap-2"
                            >
                                {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
                                Change Banner
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="px-8 pb-8">
                    <div className="flex flex-col md:flex-row justify-between items-start">
                        <div className="relative -mt-16 mb-4 md:mb-0">
                             <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden bg-white relative group">
                                <img src={displayAvatar} alt="Avatar" className="w-full h-full object-cover" />
                                {isEditing && isOwnProfile && (
                                    <div 
                                        onClick={() => !isUploading && avatarInputRef.current?.click()}
                                        className="absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition"
                                        title="Change Profile Photo"
                                    >
                                        {isUploading ? <Loader2 className="w-8 h-8 text-white animate-spin" /> : <Camera className="w-8 h-8 text-white" />}
                                    </div>
                                )}
                             </div>
                        </div>

                        <div className="pt-4 flex gap-3">
                            {isOwnProfile ? (
                                !isEditing ? (
                                    <button 
                                        onClick={() => setIsEditing(true)}
                                        className="border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 flex items-center gap-2 shadow-sm"
                                    >
                                        <Edit2 className="w-4 h-4" /> Edit Profile
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => setIsEditing(false)}
                                            className="border border-gray-300 bg-white text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <X className="w-4 h-4" /> Cancel
                                        </button>
                                        <button 
                                            onClick={handleSave}
                                            disabled={isUploading}
                                            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2 shadow-sm disabled:opacity-50"
                                        >
                                            {isUploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4" />} Save Changes
                                        </button>
                                    </div>
                                )
                            ) : (
                                // Connection Button Logic for Other Users
                                <>
                                    {isConnected ? (
                                        <button 
                                            onClick={() => toggleConnection(profileUser.id)}
                                            className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-sm transition bg-white border border-blue-200 text-blue-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                                        >
                                            <UserCheck className="w-4 h-4" /> Connected
                                        </button>
                                    ) : isPendingOutgoing ? (
                                        <button 
                                            onClick={() => toggleConnection(profileUser.id)} // Will cancel request
                                            className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-sm transition bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600"
                                        >
                                            <Clock className="w-4 h-4" /> Request Sent
                                        </button>
                                    ) : isPendingIncoming ? (
                                        <div className="flex gap-2">
                                            <button 
                                                onClick={() => acceptConnectionRequest(profileUser.id)}
                                                className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-sm transition bg-blue-600 text-white hover:bg-blue-700"
                                            >
                                                <Check className="w-4 h-4" /> Accept
                                            </button>
                                            <button 
                                                onClick={() => removeConnection(profileUser.id)}
                                                className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-sm transition bg-white border border-gray-300 text-gray-600 hover:bg-gray-100"
                                            >
                                                Decline
                                            </button>
                                        </div>
                                    ) : (
                                        <button 
                                            onClick={() => toggleConnection(profileUser.id)}
                                            className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-sm transition bg-blue-600 text-white hover:bg-blue-700"
                                        >
                                            <UserPlus className="w-4 h-4" /> Connect
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="mt-4">
                        {isEditing && isOwnProfile ? (
                            <div className="space-y-6 max-w-2xl">
                                {/* Basic Info */}
                                <div className="space-y-4">
                                    <h3 className="font-bold text-gray-900 border-b pb-1 mb-2">Basic Info</h3>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase">Full Name</label>
                                        <input 
                                            type="text" 
                                            className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 mt-1 focus:outline-none focus:border-blue-600 transition-colors"
                                            value={formData.name}
                                            onChange={e => setFormData({...formData, name: e.target.value})}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase">Voluntary Call (Role)</label>
                                        <input 
                                            type="text" 
                                            className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 mt-1 focus:outline-none focus:border-blue-600 transition-colors"
                                            value={formData.voluntaryCall}
                                            onChange={e => setFormData({...formData, voluntaryCall: e.target.value})}
                                        />
                                    </div>
                                     
                                    {/* Location with Google Maps Integration */}
                                    <div className="relative">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="text-xs font-bold text-gray-500 uppercase">Location (Google Maps)</label>
                                            <button 
                                                onClick={handleGeolocation}
                                                disabled={isLoadingGeo}
                                                className="flex items-center gap-1 text-[10px] text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 disabled:opacity-50"
                                            >
                                                {isLoadingGeo ? <Loader2 className="w-3 h-3 animate-spin"/> : <Navigation className="w-3 h-3"/>}
                                                Use Current Location
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input 
                                                type="text" 
                                                className="w-full bg-transparent border-b border-gray-300 rounded-none pl-8 pr-2 py-2 mt-1 focus:outline-none focus:border-blue-600 transition-colors"
                                                value={formData.location}
                                                onChange={e => {
                                                    setFormData({...formData, location: e.target.value});
                                                    setShowLocationSuggestions(true);
                                                }}
                                                placeholder="Search for your city..."
                                                onFocus={() => setShowLocationSuggestions(true)}
                                                onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                                            />
                                            <MapPin className="w-4 h-4 text-gray-400 absolute left-1 top-4" />
                                            {showLocationSuggestions && formData.location.length > 2 && (
                                                <div className="absolute z-10 w-full bg-white shadow-xl rounded-b-lg border border-gray-100 max-h-48 overflow-y-auto mt-1">
                                                     {isSearchingLocation && (
                                                         <div className="p-3 text-center text-gray-400 text-xs flex items-center justify-center gap-2">
                                                             <Loader2 className="w-3 h-3 animate-spin"/> Finding places...
                                                         </div>
                                                     )}
                                                     {!isSearchingLocation && locationSuggestions.map((loc, idx) => (
                                                         <div 
                                                            key={idx}
                                                            className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-sm text-gray-700"
                                                            onClick={() => {
                                                                setFormData({...formData, location: loc});
                                                                setShowLocationSuggestions(false);
                                                            }}
                                                         >
                                                             <MapPin className="w-3 h-3 text-red-500" />
                                                             {loc}
                                                         </div>
                                                     ))}
                                                     <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400 bg-gray-50 flex items-center gap-1">
                                                         <Globe className="w-3 h-3" /> Powered by Google Maps
                                                     </div>
                                                </div>
                                            )}
                                        </div>
                                        {/* Google Maps Embed Preview */}
                                        {formData.location && (
                                            <div className="mt-3 rounded-lg overflow-hidden border border-gray-200 h-32">
                                                <iframe 
                                                    width="100%" 
                                                    height="100%" 
                                                    frameBorder="0" 
                                                    scrolling="no" 
                                                    marginHeight={0} 
                                                    marginWidth={0} 
                                                    src={`https://maps.google.com/maps?q=${encodeURIComponent(formData.location)}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                                                ></iframe>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase">Bio</label>
                                        <textarea 
                                            className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 mt-1 focus:outline-none focus:border-blue-600 transition-colors h-20"
                                            value={formData.bio}
                                            onChange={e => setFormData({...formData, bio: e.target.value})}
                                        />
                                    </div>

                                    {/* Favorite Verse with AI Search */}
                                    <div>
                                        <label className="text-xs font-bold text-gray-500 uppercase flex justify-between items-center">
                                            Favorite Verse
                                            {!verseSearchMode && (
                                                <button 
                                                    onClick={() => setVerseSearchMode(true)}
                                                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 normal-case font-normal"
                                                >
                                                    <Search className="w-3 h-3" /> Find Verse
                                                </button>
                                            )}
                                        </label>
                                        
                                        {!verseSearchMode ? (
                                            <input 
                                                type="text" 
                                                className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 mt-1 focus:outline-none focus:border-blue-600 transition-colors italic text-gray-600"
                                                value={formData.favoriteBiblePassage}
                                                readOnly
                                                placeholder="Select a verse..."
                                            />
                                        ) : (
                                            <div className="bg-blue-50 p-4 rounded-lg mt-2 border border-blue-100 animate-fadeIn">
                                                <div className="flex justify-between items-center mb-2">
                                                    <h4 className="text-sm font-bold text-blue-900 flex items-center gap-2">
                                                        <Sparkles className="w-4 h-4" /> AI Verse Finder
                                                    </h4>
                                                    <button onClick={() => setVerseSearchMode(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4"/></button>
                                                </div>
                                                <div className="flex gap-2 mb-3">
                                                    <input 
                                                        type="text" 
                                                        placeholder="e.g., Strength in hard times, Love..." 
                                                        className="flex-1 bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                                                        value={verseQuery}
                                                        onChange={(e) => setVerseQuery(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleVerseSearch()}
                                                    />
                                                    <button 
                                                        onClick={handleVerseSearch}
                                                        disabled={isSearchingVerse || !verseQuery}
                                                        className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-bold disabled:opacity-50"
                                                    >
                                                        {isSearchingVerse ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Search'}
                                                    </button>
                                                </div>
                                                
                                                {foundVerse && (
                                                    <div className="bg-white p-3 rounded border border-gray-200 mb-2">
                                                        <p className="text-sm text-gray-800 italic mb-1">"{foundVerse.text}"</p>
                                                        <p className="text-xs font-bold text-blue-600 text-right">{foundVerse.reference}</p>
                                                        <button 
                                                            onClick={applyVerse}
                                                            className="w-full mt-2 bg-green-50 text-green-700 border border-green-200 text-xs font-bold py-1 rounded hover:bg-green-100"
                                                        >
                                                            Use this Verse
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Church Selector */}
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                    <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                        <Globe className="w-5 h-5 text-blue-600" /> Find Your Church Family
                                    </h3>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Country</label>
                                            <select 
                                                className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                                value={selectedCountry}
                                                onChange={(e) => {
                                                    setSelectedCountry(e.target.value);
                                                    setSelectedRegion('');
                                                }}
                                            >
                                                <option value="">All Countries</option>
                                                {availableCountries.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Region / State</label>
                                            <select 
                                                className="w-full bg-transparent border-b border-gray-300 rounded-none px-2 py-2 focus:outline-none focus:border-blue-600 transition-colors"
                                                value={selectedRegion}
                                                onChange={(e) => setSelectedRegion(e.target.value)}
                                                disabled={!selectedCountry}
                                            >
                                                <option value="">All Regions</option>
                                                {availableRegions.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="relative mb-3">
                                        <input 
                                            type="text"
                                            placeholder="Search church name..."
                                            className="w-full pl-9 pr-3 py-2 bg-transparent border-b border-gray-300 rounded-none focus:outline-none focus:border-blue-600 transition-colors"
                                            value={churchSearch}
                                            onChange={(e) => setChurchSearch(e.target.value)}
                                        />
                                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                        {filteredChurches.map(c => (
                                            <div 
                                                key={c.id}
                                                onClick={() => setFormData({...formData, churchId: c.id})}
                                                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                                    formData.churchId === c.id 
                                                    ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20' 
                                                    : 'bg-white border-gray-200 hover:border-blue-300'
                                                }`}
                                            >
                                                <img src={c.logoUrl} className="w-10 h-10 rounded-full bg-gray-100 object-cover" alt="" />
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-bold truncate ${formData.churchId === c.id ? 'text-blue-700' : 'text-gray-900'}`}>{c.name}</p>
                                                    <p className="text-xs text-gray-500 truncate">{c.region}, {c.country}</p>
                                                </div>
                                                {formData.churchId === c.id && <Check className="w-5 h-5 text-blue-600" />}
                                            </div>
                                        ))}
                                        {filteredChurches.length === 0 && (
                                            <div className="col-span-full text-center py-4 text-gray-500 italic text-sm">
                                                No churches found matching your filters.
                                            </div>
                                        )}
                                    </div>
                                    {formData.churchId && (
                                        <p className="text-xs text-green-600 font-bold mt-2 flex items-center gap-1">
                                            <Check className="w-3 h-3" /> Selected: {churches.find(c => c.id === formData.churchId)?.name}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <h1 className="text-3xl font-bold text-gray-900">{profileUser.name}</h1>
                                <p className="text-lg text-blue-600 font-medium flex items-center gap-2 mt-1">
                                    <Briefcase className="w-4 h-4" /> {profileUser.voluntaryCall}
                                </p>
                                <div className="flex flex-wrap items-center gap-4 mt-2 text-gray-500">
                                    <p className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" /> {profileUser.location}
                                    </p>
                                    <p className="flex items-center gap-1 font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full text-xs">
                                        <Users className="w-3.5 h-3.5" /> {connectionCount} Connections
                                    </p>
                                </div>
                                <p className="text-gray-700 mt-4 max-w-2xl leading-relaxed">
                                    {profileUser.bio || "No bio added yet."}
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 max-w-2xl">
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-center gap-3">
                                        <div className="bg-white p-2 rounded-full shadow-sm">
                                            {church ? (
                                                <img src={church.logoUrl} className="w-8 h-8 rounded-full" alt="Church Logo" />
                                            ) : (
                                                <Home className="w-5 h-5 text-blue-600" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="text-xs font-bold text-blue-800 uppercase mb-0.5">Church Family</h3>
                                            <p className="font-semibold text-gray-900">{church?.name || "No Church Selected"}</p>
                                            {church && <p className="text-xs text-gray-500">{church.address}</p>}
                                        </div>
                                    </div>
                                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 flex items-center gap-3">
                                        <div className="bg-white p-2 rounded-full shadow-sm">
                                            <Book className="w-5 h-5 text-yellow-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-xs font-bold text-yellow-800 uppercase mb-0.5">Life Verse</h3>
                                            <p className="font-serif italic text-gray-900">"{profileUser.favoriteBiblePassage}"</p>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Tabs / Feed */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6 border-b pb-2">My Journey</h3>
                {userPosts.length > 0 ? (
                    <div className="space-y-6">
                        {userPosts.map(post => {
                            const isLiked = currentUser ? post.likes.includes(currentUser.id) : false;
                            
                            return (
                                <div key={post.id} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0 relative group">
                                    {isOwnProfile && (
                                        <button 
                                            onClick={() => { if(window.confirm('Delete this post?')) removePost(post.id); }}
                                            className="absolute top-0 right-0 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
                                            title="Delete Post"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                    <div className="flex items-center mb-2 gap-2">
                                        <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded">{new Date(post.timestamp).toLocaleDateString()}</span>
                                        {post.bibleCitation && <span className="text-[10px] text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded">Verse Shared</span>}
                                    </div>
                                    
                                    <p className="text-gray-800 mb-3 whitespace-pre-wrap">{post.content}</p>
                                    
                                    {post.bibleCitation && (
                                        <div className="bg-gray-50 border-l-4 border-gray-300 p-3 mb-3 rounded-r">
                                            <p className="italic text-gray-600 font-serif text-sm">"{post.bibleCitation.text}"</p>
                                            <p className="text-xs font-bold text-gray-500 mt-1">— {post.bibleCitation.reference}</p>
                                        </div>
                                    )}

                                    {post.imageUrl && <img src={post.imageUrl} className="rounded-lg max-h-64 object-cover mb-3" alt="" />}
                                    
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                        <button 
                                            onClick={() => toggleLike(post.id)} 
                                            className={`flex items-center gap-1 hover:text-red-500 transition ${isLiked ? 'text-red-500' : ''}`}
                                        >
                                            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} /> {post.likes.length}
                                        </button>
                                        <span className="flex items-center gap-1">
                                            <MessageCircle className="w-4 h-4" /> {post.comments.length}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-10 text-gray-500 italic">
                        No posts shared yet.
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;
